import React, { useMemo } from 'react';

import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import ExportView from '@dashboard/components/ExportView';
import SearchInput from '@dashboard/components/Form/components/SearchInput';
import FilterInput from '@dashboard/components/Form/components/FilterInput';

import useUrlState from '@dashboard/hooks/useUrlState';
import useTableSort from '@dashboard/hooks/useTableSort';
import GuestsList from '../components/GuestsList';
import { GUEST_FILTER_OPTIONS } from './PageBookings';

const GUESTS_DEFAULT_SORT = { id: 'orderedAt', desc: true };

const PageGuests = ({ page }) => {
  const [query, setParams] = useUrlState();
  const { sorting, onSortingChange, sort } = useTableSort({
    defaultSort: GUESTS_DEFAULT_SORT,
  });
  const ticketGroups = useMemo(() => page.ticketGroups || [], [page.ticketGroups]);

  const search = query.search || '';
  const ticketGroupFilter = query.ticketGroup || 'ALL';
  const guestStatusFilter = query.guestStatus || 'ALL';
  const guestCheckInStatusFilter = query.guestCheckInStatus || 'ALL';
  const collectsGuests = Boolean(page.eventDetails?.attendeeDetails?.enabled);
  // Links saved before the rename still say ?guests=.
  const attendeesParam = query.attendees ?? query.guests;
  const guestDetailsFilter =
    collectsGuests && GUEST_FILTER_OPTIONS[attendeesParam] ? attendeesParam : 'ALL';

  const ticketGroupOptions = useMemo(
    () => ({
      ALL: {
        value: '',
        label: 'All ticket groups',
      },
      ...((ticketGroups || []).reduce((acc, group) => {
        acc[group._id] = {
          value: group._id,
          label: group.name,
        };
        return acc;
      }, {}) || {}),
    }),
    [ticketGroups],
  );

  const selectedTicketGroupId = ticketGroupOptions[ticketGroupFilter]?.value;

  const ticketTypeOptions = useMemo(
    () => ({
      ALL: {
        value: '',
        label: 'All tickets',
      },
      ...((ticketGroups || []).reduce((acc, group) => {
        // If a specific ticket group is selected, only show ticket types from that group
        if (ticketGroupFilter !== 'ALL' && group._id !== selectedTicketGroupId) {
          return acc;
        }

        group.ticketTypes?.forEach(ticketType => {
          acc[ticketType._id] = {
            value: ticketType._id,
            label: `${ticketType.name} (${group.name})`,
          };
        });
        return acc;
      }, {}) || {}),
    }),
    [selectedTicketGroupId, ticketGroupFilter, ticketGroups],
  );

  const guestStatusOptions = {
    ALL: {
      value: '',
      label: 'All statuses',
    },
    VALID: {
      value: 'PAID',
      label: 'Valid',
    },
    CANCELED: {
      value: 'CANCELED',
      label: 'Canceled',
    },
  };

  const guestCheckedInStatusOptions = {
    ALL: {
      value: '',
      label: 'All check ins',
    },
    UNCHECKEDIN: {
      value: 'UNCHECKEDIN',
      label: 'Pending',
    },
    CHECKEDIN: {
      value: 'CHECKEDIN',
      label: 'Checked in',
    },
  };

  // If the URL has a ticketType that's not valid for the current group, treat as ALL
  const effectiveTicketType = ticketTypeOptions[query.ticketType] ? query.ticketType : 'ALL';

  const filter = {
    ...(ticketGroupOptions[ticketGroupFilter]?.value && {
      ticketGroup: ticketGroupOptions[ticketGroupFilter].value,
    }),
    ...(ticketTypeOptions[effectiveTicketType]?.value && {
      ticketType: ticketTypeOptions[effectiveTicketType].value,
    }),
    ...(guestStatusOptions[guestStatusFilter]?.value && {
      status: guestStatusOptions[guestStatusFilter].value,
    }),
    ...(guestCheckedInStatusOptions[guestCheckInStatusFilter]?.value && {
      checkedInStatus: guestCheckedInStatusOptions[guestCheckInStatusFilter].value,
    }),
    ...(GUEST_FILTER_OPTIONS[guestDetailsFilter]?.value && {
      guestDetails: GUEST_FILTER_OPTIONS[guestDetailsFilter].value,
    }),
  };

  let title = 'customers';
  if (page.type === 'form') {
    title = 'leads';
  } else if (page.type === 'event') {
    title = 'guests';
  }

  const handleSearchChange = value => setParams({ search: value || undefined });

  const handleTicketGroupChange = key =>
    setParams({
      ticketGroup: key === 'ALL' ? undefined : key,
      ticketType: undefined, // reset ticket type when group changes
    });

  const handleTicketTypeChange = key => setParams({ ticketType: key === 'ALL' ? undefined : key });

  const handleGuestStatusChange = key =>
    setParams({ guestStatus: key === 'ALL' ? undefined : key });

  const handleGuestDetailsChange = key =>
    setParams({ attendees: key === 'ALL' ? undefined : key, guests: undefined });

  const handleGuestCheckInStatusChange = key =>
    setParams({ guestCheckInStatus: key === 'ALL' ? undefined : key });

  return (
    <div className="mb-24">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 pb-4">
        <CardHeaderTitle variant="page">{collectsGuests ? 'Attendees' : 'Guests'}</CardHeaderTitle>

        <div className="flex flex-wrap items-center gap-2 max-lg:w-full [&>*:first-child]:min-w-0 max-lg:[&>*:first-child]:flex-1 max-lg:[&>*:first-child]:basis-56 max-sm:[&>*:first-child]:basis-full">
          <SearchInput
            onChange={handleSearchChange}
            name="search_customers"
            placeholder="Search by name, email or code"
            className="w-66"
            initialValue={search}
          />

          <FilterInput
            options={ticketGroupOptions}
            onChange={handleTicketGroupChange}
            value={ticketGroupFilter}
          />

          <FilterInput
            options={ticketTypeOptions}
            onChange={handleTicketTypeChange}
            value={effectiveTicketType}
          />

          {collectsGuests ? (
            <FilterInput
              options={GUEST_FILTER_OPTIONS}
              onChange={handleGuestDetailsChange}
              value={guestDetailsFilter}
            />
          ) : null}

          <FilterInput
            options={guestStatusOptions}
            onChange={handleGuestStatusChange}
            value={guestStatusFilter}
          />

          <FilterInput
            options={guestCheckedInStatusOptions}
            onChange={handleGuestCheckInStatusChange}
            value={guestCheckInStatusFilter}
          />

          <ExportView
            collection="attendee-list"
            label={title}
            filters={{
              pageId: page._id,
              ...filter,
              search: search || undefined,
              sort: JSON.stringify(sort),
            }}
          />
        </div>
      </div>

      <div className="overflow-auto mb-20">
        <GuestsList
          sort={sort}
          search={search}
          filter={filter}
          sorting={sorting}
          onSortingChange={onSortingChange}
          collectsGuests={collectsGuests}
        />
      </div>
    </div>
  );
};

export default PageGuests;
